// Camera + file-upload helpers for the photo-based capture flow.
//
// We keep the raw MediaStream alive only while the preview is on screen;
// once the user snaps, we freeze a single frame into an ImageBitmap and
// stop all tracks so the camera LED goes off immediately. That is the
// user-visible signal that "no more video is being captured" and matters
// for the local-first privacy story even though we never send frames.

import { copyNow } from '../i18n/vtoCopy';

export interface CaptureResult {
  bitmap: ImageBitmap;
  width: number;
  height: number;
  // Original orientation-corrected image, mirrored for front camera so the
  // user sees themselves the way they expect.
}

export interface CaptureFrameOptions {
  maxSide?: number;
  mirror?: boolean;
}

// Scale (w,h) down so the longest side is at most maxSide, preserving aspect
// ratio. Returns the original dims when maxSide is falsy or already within range.
// Shared by the file-decode and live-camera capture paths.
export function fitWithin(
  w: number,
  h: number,
  maxSide: number | undefined
): { width: number; height: number } {
  const longest = Math.max(w, h);
  const scale = maxSide && longest > maxSide ? maxSide / longest : 1;
  return {
    width: Math.max(1, Math.round(w * scale)),
    height: Math.max(1, Math.round(h * scale))
  };
}

const CAMERA_PROFILES: MediaTrackConstraints[] = [
  {
    facingMode: 'user',
    width: { ideal: 1920 },
    height: { ideal: 1440 },
    frameRate: { ideal: 30, max: 30 },
    aspectRatio: { ideal: 0.75 }
  },
  {
    facingMode: 'user',
    width: { ideal: 1440 },
    height: { ideal: 1080 },
    frameRate: { ideal: 30, max: 30 }
  },
  {
    facingMode: 'user',
    width: { ideal: 1280 },
    height: { ideal: 1280 }
  }
];

function waitForVideoReady(video: HTMLVideoElement): Promise<void> {
  if (video.videoWidth > 0 && video.videoHeight > 0) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      cleanup();
      reject(new Error(copyNow().capture.errors.videoNotReady));
    }, 3500);

    function cleanup() {
      window.clearTimeout(timeout);
      video.removeEventListener('loadedmetadata', onReady);
      video.removeEventListener('canplay', onReady);
    }

    function onReady() {
      cleanup();
      resolve();
    }

    video.addEventListener('loadedmetadata', onReady, { once: true });
    video.addEventListener('canplay', onReady, { once: true });
  });
}

export async function startCameraPreview(
  video: HTMLVideoElement
): Promise<MediaStream> {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error(copyNow().capture.errors.cameraUnsupported);
  }

  let stream: MediaStream | null = null;
  let lastError: unknown = null;
  for (const profile of CAMERA_PROFILES) {
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: profile
      });
      break;
    } catch (err) {
      lastError = err;
    }
  }
  if (!stream) throw lastError instanceof Error ? lastError : new Error(copyNow().capture.errors.cameraUnavailable);

  video.srcObject = stream;
  video.playsInline = true;
  video.muted = true;
  try {
    await video.play();
    await waitForVideoReady(video);
  } catch (err) {
    stopCameraPreview(stream);
    throw err;
  }
  return stream;
}

export function stopCameraPreview(stream: MediaStream | null): void {
  stream?.getTracks().forEach((t) => t.stop());
}

// iOS Safari/WKWebView caps total canvas area (~16.7M px) and kills the tab when
// an image blows the per-tab memory budget. A modern iPhone photo is 12MP and the
// Pro models shoot 48MP (8064×6048 ≈ 48.7M px) — decoding one at native resolution
// into a full-size canvas + ImageBitmap crashes the VTO mid-processing. Cap the
// longest side so the whole pipeline stays within budget; the matting/face ONNX
// models downscale to 512px anyway and the on-screen target is ≤1920.
const MAX_DECODE_SIDE = 1280;

async function bitmapFromDecodedImage(source: Blob | File): Promise<CaptureResult> {
  const objectUrl = URL.createObjectURL(source);
  try {
    const img = new Image();
    img.decoding = 'async';
    img.src = objectUrl;
    await img.decode();

    const natW = img.naturalWidth || img.width;
    const natH = img.naturalHeight || img.height;
    if (!natW || !natH) throw new Error('Decoded image has no dimensions');

    // Downscale during drawImage so the canvas (the hard iOS limit) is never
    // allocated at the photo's native megapixels.
    const { width, height } = fitWithin(natW, natH, MAX_DECODE_SIDE);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('2D context unavailable');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, width, height);

    const bitmap = await createImageBitmap(canvas);
    return { bitmap, width, height };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export async function captureVideoFrame(
  video: HTMLVideoElement,
  options: CaptureFrameOptions = {}
): Promise<CaptureResult> {
  const w = video.videoWidth;
  const h = video.videoHeight;
  if (!w || !h) throw new Error('Video not ready');

  const { width: outW, height: outH } = fitWithin(w, h, options.maxSide);
  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d', { alpha: false });
  if (!ctx) throw new Error('2D context unavailable');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  if (options.mirror ?? true) {
    // Mirror horizontally to match the preview (CSS transform: scaleX(-1)
    // is applied to the <video> element in Capture.svelte).
    ctx.translate(outW, 0);
    ctx.scale(-1, 1);
  }
  ctx.drawImage(video, 0, 0, outW, outH);

  const bitmap = await createImageBitmap(canvas);
  return { bitmap, width: outW, height: outH };
}

// Captures the current video frame into an ImageBitmap, mirroring it so
// downstream rendering uses the same orientation as the preview showed.
// Capped to MAX_DECODE_SIDE: the Canvas2D look-compositing chain allocates ~7
// full-snapshot-resolution canvases that coexist on iOS (GPU memory not freed
// until GC), so a smaller snapshot keeps memory-constrained iPhones (e.g. the
// 6 GB 13 Pro) under the WKWebView canvas budget instead of OOM/black-render.
export async function captureFromVideo(
  video: HTMLVideoElement
): Promise<CaptureResult> {
  return captureVideoFrame(video, { maxSide: MAX_DECODE_SIDE });
}

// Loads a user-selected file into an ImageBitmap. Applies EXIF orientation
// through the browser image decoder, then flattens the result into a canvas.
// This avoids platform-specific ImageBitmap orientation quirks on iOS Safari.
export async function captureFromFile(file: File): Promise<CaptureResult> {
  return bitmapFromDecodedImage(file);
}

// Loads a same-origin image URL into an ImageBitmap — used by the built-in
// demo photos (20/30/40/50 reference portraits) so the user can preview the
// VTO without having to take a selfie first.
export async function captureFromUrl(url: string): Promise<CaptureResult> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Cannot load demo photo: ${res.status}`);
  const blob = await res.blob();
  return bitmapFromDecodedImage(blob);
}
